import React, { useMemo, useState } from "react";
import type { DBProject } from "@/lib/db-types";
import type { BudgetLineRow, OfferMetadata } from "@/hooks/use-project-detail";
import { formatNOK } from "@/lib/format";
import { calcLiveBfEstimated, isSummaryLine, isUpsellLine } from "@/lib/billing-calculations";
import { PHASE_LABELS, type ProjectPhase } from "@/lib/offer-types";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCommitBudgetRevision } from "@/hooks/use-budget-revision";
import {
  evaluateDeletion,
  evaluateHoursChange,
  evaluateDescriptionChange,
  type LineGuardContext,
  type DeletionVerdict,
} from "@/lib/budget-revision";
import {
  toDraft,
  draftToBfLine,
  buildRevisionChanges,
  type RevisionDraftLine,
} from "@/lib/budget-revision-draft";
import { BudgetRevisionRow } from "./BudgetRevisionRow";
import { BudgetRevisionCommitDialog } from "./BudgetRevisionCommitDialog";

/**
 * VIB-1202: Revisjonsmodus for et godkjent hovedbudsjett. Samler endringer
 * lokalt (draft state — ingen skriving før commit), viser live sum-/BF-effekt,
 * håndhever vernet (reduksjon under loggede/fullførte timer krever bekreftelse,
 * låst måned blokkeres, sletting av linje med loggede timer blokkeres), og
 * committer alt som én sekvens + ett `budget_revised`-event via
 * `useCommitBudgetRevision`. `budget_line_baseline` røres aldri. Ren draft-/
 * commit-logikk bor i `src/lib/budget-revision-draft.ts` (testbar).
 */

const EMPTY_GUARD: LineGuardContext = {
  loggedHours: 0,
  completedApprovedHours: 0,
  hasLockedMonthCompletion: false,
};

/**
 * Brukermelding per blokkerings-verdict. Typet som Record over ALLE
 * ikke-`ok`-verdicts (Exclude<DeletionVerdict, "ok">), så en ny verdict i
 * `evaluateDeletion` tvinger en kompileringsfeil her — vernet kan ikke lenger
 * lekke fordi kallstedet blokkerer på ALLE ikke-`ok`, ikke en enkelt literal.
 */
const DELETE_BLOCK_MESSAGES: Record<Exclude<DeletionVerdict, "ok">, string> = {
  blocked_logged_hours:
    "Kan ikke slette en linje med loggede timer. Omfordel timene først.",
  blocked_completed_assignments:
    "Kan ikke slette en linje med fullførte/godkjente tildelinger (de teller i faktureringsgrad). Nullstill tildelingene først.",
  blocked_month_lock:
    "Kan ikke slette en linje med en fullført tildeling i en lukket måned.",
};

export function BudgetRevisionMode({
  project,
  budgetLines,
  offerMetadata,
  guards,
  onExit,
}: {
  project: DBProject;
  budgetLines: BudgetLineRow[];
  offerMetadata: OfferMetadata | null;
  guards: Map<string, LineGuardContext>;
  onExit: () => void;
}) {
  const { user } = useAuth();
  const commit = useCommitBudgetRevision();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Revisjon gjelder hovedbudsjettets linjer (ikke mersalg). Summary-linjer
  // (overhead/rabatt) er metadata og redigeres ikke her.
  const editableOriginals = useMemo(
    () => budgetLines.filter((l) => !isUpsellLine(l) && l.phase !== "mersalg" && !isSummaryLine(l)),
    [budgetLines],
  );
  const summaryLines = useMemo(
    () => budgetLines.filter((l) => !isUpsellLine(l) && l.phase !== "mersalg" && isSummaryLine(l)),
    [budgetLines],
  );

  const [drafts, setDrafts] = useState<RevisionDraftLine[]>(() => editableOriginals.map(toDraft));

  const originalById = useMemo(
    () => new Map(editableOriginals.map((l) => [l.id, l])),
    [editableOriginals],
  );

  const updateDraft = (key: string, patch: Partial<RevisionDraftLine>) =>
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));

  const addLine = (phase: ProjectPhase) =>
    setDrafts((prev) => [
      ...prev,
      {
        key: `new-${crypto.randomUUID()}`,
        id: null,
        isNew: true,
        deleted: false,
        description: "Ny linje",
        phase,
        cost_type: "internal",
        quantity: null,
        quantity_hours: 0,
        amount: 0,
        estimated_cost: 0,
        template_id: null,
      },
    ]);

  const toggleDelete = (key: string) => {
    const d = drafts.find((x) => x.key === key);
    if (!d) return;
    if (d.isNew) {
      setDrafts((prev) => prev.filter((x) => x.key !== key));
      return;
    }
    if (!d.deleted && d.id) {
      const guard = guards.get(d.id) ?? EMPTY_GUARD;
      const verdict = evaluateDeletion(guard);
      if (verdict !== "ok") {
        toast.error(DELETE_BLOCK_MESSAGES[verdict]);
        return;
      }
    }
    updateDraft(key, { deleted: !d.deleted });
  };

  const live = drafts.filter((d) => !d.deleted);
  const bfLines = [...live.map(draftToBfLine), ...summaryLines];
  const bfCalc = calcLiveBfEstimated(bfLines as never, offerMetadata);

  const totalBefore = Number(project.total_amount ?? 0);
  const totalAfter = bfCalc.liveTotalAmount;
  const bfBefore = calcLiveBfEstimated(
    [...editableOriginals, ...summaryLines] as never,
    offerMetadata,
  ).liveBfEstimated;
  const bfAfter = bfCalc.liveBfEstimated;

  // Vern: samle blokkerte/bekreftelses-krevende endringer. Timeendring kan
  // blokkeres (låst måned) eller kreve bekreftelse (reduksjon under gulvet);
  // description-endring på en linje med loggede timer krever bekreftelse
  // (string-match-koblingen kan miste sporet).
  const changeVerdicts = live
    .filter((d) => !d.isNew && d.id)
    .flatMap((d) => {
      const orig = originalById.get(d.id!);
      const guard = guards.get(d.id!) ?? EMPTY_GUARD;
      const verdicts: Array<"blocked_month_lock" | "needs_confirmation"> = [];
      if ((orig?.quantity_hours ?? 0) !== (d.quantity_hours ?? 0)) {
        const v = evaluateHoursChange(d.quantity_hours ?? 0, guard);
        if (v !== "ok") verdicts.push(v);
      }
      if ((orig?.description ?? "") !== d.description) {
        const v = evaluateDescriptionChange(guard);
        if (v !== "ok") verdicts.push(v);
      }
      return verdicts;
    });

  const blocked = changeVerdicts.filter((v) => v === "blocked_month_lock");
  const needsConfirm = changeVerdicts.filter((v) => v === "needs_confirmation");

  const { updates, creates, deletes, assignmentAdjustments } = buildRevisionChanges(
    drafts,
    originalById,
    project.id,
  );
  const nChanges = updates.length + creates.length + deletes.length;
  const summaryForDialog = {
    changes: [],
    updatedCount: updates.length,
    createdCount: creates.length,
    deletedCount: deletes.length,
  };

  const handleConfirm = (reason: string) => {
    commit.mutate(
      {
        projectId: project.id,
        reason,
        updates,
        creates,
        deletes,
        assignmentAdjustments,
        totalBefore,
        totalAfter,
        bfEstimatedBefore: bfBefore,
        bfEstimatedAfter: bfAfter,
        actorId: user?.id ?? null,
      },
      {
        onSuccess: () => {
          setDialogOpen(false);
          onExit();
        },
      },
    );
  };

  const phases = Object.keys(PHASE_LABELS) as ProjectPhase[];

  return (
    <div className="border-[1.5px] border-amber-500 bg-card">
      <div className="px-4 py-3 border-b-[1.5px] border-amber-500 flex items-center gap-2 bg-amber-50">
        <SrIcon name="edit" className="w-4 h-4 text-amber-700" />
        <span className="font-display font-semibold text-lg tracking-tight text-amber-900">
          Revisjonsmodus
        </span>
        <span className="ml-2 text-xs text-amber-800">
          Endringer lagres først når du bekrefter revisjonen.
        </span>
        <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={onExit}>
          Avbryt
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-muted-foreground uppercase tracking-wider border-b text-[11px]">
              <th className="text-left font-medium px-4 py-2.5">Linje</th>
              <th className="text-right font-medium px-3 py-2.5 w-24">Antall</th>
              <th className="text-right font-medium px-3 py-2.5 w-32">Beløp</th>
              <th className="text-right font-medium px-4 py-2.5 w-32">Est. kost</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {phases.map((phase) => {
              const rows = drafts.filter((d) => d.phase === phase);
              return (
                <React.Fragment key={`grp-${phase}`}>
                  <tr className="bg-muted/30">
                    <td colSpan={5} className="px-4 py-1.5 font-medium text-xs uppercase tracking-wide">
                      <div className="flex items-center justify-between">
                        <span>{PHASE_LABELS[phase]}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[11px]"
                          onClick={() => addLine(phase)}
                        >
                          <SrIcon name="plus" className="w-3 h-3 mr-1" /> Ny linje
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {rows.map((d) => (
                    <BudgetRevisionRow
                      key={d.key}
                      draft={d}
                      guard={d.id ? guards.get(d.id) ?? EMPTY_GUARD : EMPTY_GUARD}
                      originalHours={d.id ? originalById.get(d.id)?.quantity_hours ?? null : null}
                      originalDescription={d.id ? originalById.get(d.id)?.description ?? null : null}
                      onChange={(patch) => updateDraft(d.key, patch)}
                      onToggleDelete={() => toggleDelete(d.key)}
                    />
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t-[1.5px] border-amber-500 bg-amber-50/60 flex items-center gap-4 flex-wrap">
        <div className="text-sm">
          <span className="text-muted-foreground">Tilbudssum: </span>
          <span className="tabular-nums">{formatNOK(totalBefore)}</span>
          <span className="text-muted-foreground"> → </span>
          <span className={cn("tabular-nums", totalBefore !== totalAfter && "font-semibold")}>
            {formatNOK(totalAfter)}
          </span>
        </div>
        <div className="text-sm">
          <span className="text-muted-foreground">GEEK est.: </span>
          <span className="tabular-nums">{formatNOK(bfBefore)}</span>
          <span className="text-muted-foreground"> → </span>
          <span className={cn("tabular-nums", bfBefore !== bfAfter && "font-semibold")}>
            {formatNOK(bfAfter)}
          </span>
        </div>
        {blocked.length > 0 && (
          <span className="text-xs text-destructive">
            {blocked.length} endring(er) i låst måned — kan ikke lagres.
          </span>
        )}
        {needsConfirm.length > 0 && blocked.length === 0 && (
          <span className="text-xs text-amber-700">
            {needsConfirm.length} reduksjon(er) under loggede/fullførte timer krever bekreftelse.
          </span>
        )}
        <Button
          className="ml-auto"
          size="sm"
          disabled={nChanges === 0 || blocked.length > 0}
          onClick={() => setDialogOpen(true)}
        >
          Fullfør revisjon ({nChanges})
        </Button>
      </div>

      <BudgetRevisionCommitDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        summary={summaryForDialog}
        assignmentAdjustments={assignmentAdjustments}
        totalBefore={totalBefore}
        totalAfter={totalAfter}
        bfBefore={bfBefore}
        bfAfter={bfAfter}
        isPending={commit.isPending}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
